import { useState, useCallback } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";

export interface FeedbackData {
  whatWorking: string;
  whatToImprove: string;
  keyMetric: string;
  submittedAt: string;
}

interface UseFeedbackCaptureResult {
  feedback: FeedbackData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadFeedback: (userID: string, projectID: string) => Promise<void>;
  saveFeedback: (userID: string, projectID: string, data: Omit<FeedbackData, "submittedAt">) => Promise<void>;
}

/**
 * Hook for capturing post-launch feedback on a project.
 * Reads/writes to Firestore at: users/{uid}/projects/{id}/feedback (document "summary")
 */
export function useFeedbackCapture(): UseFeedbackCaptureResult {
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (userID: string, projectID: string): Promise<void> => {
    if (!userID || !projectID) return;

    setIsLoading(true);
    setError(null);

    try {
      const feedbackRef = doc(db, "users", userID, "projects", projectID, "feedback", "summary");
      const snap = await getDoc(feedbackRef);

      if (snap.exists()) {
        setFeedback(snap.data() as FeedbackData);
      } else {
        setFeedback(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load feedback";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveFeedback = useCallback(
    async (
      userID: string,
      projectID: string,
      data: Omit<FeedbackData, "submittedAt">
    ): Promise<void> => {
      if (!userID || !projectID) return;

      setIsSaving(true);
      setError(null);

      try {
        const feedbackData: FeedbackData = {
          ...data,
          submittedAt: new Date().toISOString(),
        };

        const feedbackRef = doc(db, "users", userID, "projects", projectID, "feedback", "summary");
        await setDoc(feedbackRef, feedbackData);
        setFeedback(feedbackData);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to save feedback";
        setError(msg);
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  return { feedback, isLoading, isSaving, error, loadFeedback, saveFeedback };
}
